/**
 * The Punch List tab's styles, prefix `pl-`. One block for the tab and
 * everything it opens (drawer, modals), rendered once by PunchTab as
 * <PunchStyles />: those all mount inside it, and a class prefix is a naming
 * convention, not a scope (CLAUDE.md, CSS). A component (not a bare string) so
 * styles/cssCoPresence.spec.ts sees the tab render it. Values follow the
 * design handoff's specs and tokens.css.
 */
const css = `
  .pl { display: grid; gap: var(--ew-s-4); }
  .pl-title { display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap; }
  .pl-title h2 { margin: 0; font: var(--ew-fw-semibold) 24px/1.2 var(--ew-font); color: var(--ew-dark); }
  .pl-meta { font-size: 14px; color: var(--ew-n-500); }
  .pl-card { background: #fff; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); }
  .pl-caption {
    font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: 0.08em;
    text-transform: uppercase; color: var(--ew-n-500);
  }
  .pl-muted { color: var(--ew-n-500); font-size: 13px; }
  .pl-error {
    padding: 8px 12px; background: var(--ew-danger-50); color: var(--ew-danger-700);
    border-radius: var(--ew-r-md); font-size: 13px;
  }
  .pl-note {
    padding: 8px 12px; background: var(--ew-quality-50); color: var(--ew-quality-700);
    border-radius: var(--ew-r-md); font-size: 13px;
  }
  .pl-warn {
    padding: 8px 12px; background: var(--ew-safety-50); color: var(--ew-safety-700);
    border: 1px solid var(--ew-safety-100); border-radius: var(--ew-r-md); font-size: 13px;
  }

  /* Stage banner */
  .pl-stage { padding: 16px 20px; display: grid; gap: 12px; }
  .pl-stage-top { display: flex; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
  .pl-steps { display: flex; align-items: center; flex-wrap: wrap; row-gap: 8px; }
  .pl-step { display: flex; align-items: center; gap: 8px; font-size: 15px; color: var(--ew-n-500); }
  .pl-step.current { color: var(--ew-dark); font-weight: 600; }
  .pl-step-box {
    width: 24px; height: 24px; border-radius: var(--ew-r-sm); display: inline-flex; align-items: center; justify-content: center;
    font: var(--ew-fw-semibold) 12px/1 var(--ew-font); border: 1px solid var(--ew-n-300); background: #fff; color: var(--ew-n-500);
  }
  .pl-step.done .pl-step-box { background: var(--ew-green); border-color: var(--ew-green); color: #fff; }
  .pl-step.current .pl-step-box { background: var(--ew-dark); border-color: var(--ew-dark); color: #fff; }
  .pl-step-line { width: 40px; height: 2px; background: var(--ew-n-200); margin: 0 10px; }
  .pl-stage-desc { font-size: 14px; color: var(--ew-n-500); margin-top: 6px; }
  .pl-stage-actions { display: flex; gap: 8px; align-items: flex-start; flex-wrap: wrap; }
  .pl-progress { display: flex; align-items: center; gap: 12px; }
  .pl-bar { flex: 1; height: 8px; border-radius: var(--ew-r-pill); background: var(--ew-n-100); overflow: hidden; }
  .pl-bar > span { display: block; height: 100%; background: var(--ew-green); border-radius: var(--ew-r-pill); }
  .pl-progress-label { font: var(--ew-fw-medium) 13px/1 var(--ew-font); white-space: nowrap; }

  /* KPI row — reuses .pw-kpi from the workspace */
  .pl-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 12px; }
  .pl-kpi-btn { all: unset; cursor: pointer; display: block; }
  .pl-kpi-btn:focus-visible { outline: 2px solid var(--ew-green); outline-offset: 2px; }
  .pl-kpi-sub { display: block; font-size: 13px; color: var(--ew-n-500); margin-top: 4px; }
  .pl-kpi-btn.active .pw-kpi { border-color: var(--ew-green); background: var(--ew-green-50); }

  /* Ball in court + by sub */
  .pl-two { display: grid; grid-template-columns: 1fr 1.5fr; gap: 16px; }
  @media (max-width: 900px) { .pl-two { grid-template-columns: 1fr; } }
  .pl-card-hd { padding: 12px 16px; border-bottom: 1px solid var(--ew-n-100); }
  .pl-bic-row {
    all: unset; box-sizing: border-box; width: 100%; cursor: pointer; display: grid;
    grid-template-columns: 44px 1fr auto; gap: 12px; align-items: center; padding: 12px 16px;
    border-bottom: 1px solid var(--ew-n-100);
  }
  .pl-bic-row:last-child { border-bottom: 0; }
  .pl-bic-row:hover { background: var(--ew-n-50); }
  .pl-bic-row.active { background: var(--ew-green-50); }
  .pl-bic-label { font: var(--ew-fw-medium) 15px/1.3 var(--ew-font); color: var(--ew-dark); }
  .pl-bic-sub { font-size: 13px; color: var(--ew-n-500); }
  .pl-bic-count { font: var(--ew-fw-semibold) 24px/1 var(--ew-font); font-variant-numeric: tabular-nums; }
  .pl-sub-grid { display: grid; grid-template-columns: 1.6fr 52px 60px 64px 1fr; gap: 8px; align-items: center; }
  .pl-sub-head { padding: 8px 16px; background: var(--ew-n-50); }
  .pl-sub-list { max-height: 236px; overflow-y: auto; }
  .pl-sub-row {
    all: unset; box-sizing: border-box; width: 100%; cursor: pointer; padding: 10px 16px;
    border-top: 1px solid var(--ew-n-100); font-size: 14px; font-variant-numeric: tabular-nums;
  }
  .pl-sub-line { display: flex; align-items: center; gap: 8px; padding-right: 12px; border-top: 1px solid var(--ew-n-100); }
  .pl-sub-line > .pl-sub-row { flex: 1; border-top: 0; }
  .pl-sub-line.active { background: var(--ew-green-50); }
  .pl-sub-head { padding-right: 84px; }
  .pl-sub-row:hover { background: var(--ew-n-50); }
  .pl-sub-row.active { background: var(--ew-green-50); }
  .pl-sub-name { font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .pl-mini-bar { display: flex; align-items: center; gap: 6px; font-size: 12px; color: var(--ew-n-500); }
  .pl-mini-bar > span:first-child { flex: 1; height: 8px; border-radius: var(--ew-r-pill); background: var(--ew-n-100); overflow: hidden; }
  .pl-mini-bar > span:first-child > span { display: block; height: 100%; background: var(--ew-green); }
  .pl-overdue { color: var(--ew-danger-700); font-weight: 600; }

  /* Filters */
  .pl-filters { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
  .pl-input, .pl-select {
    height: 36px; box-sizing: border-box; border: 1px solid var(--ew-n-300); border-radius: var(--ew-r-md);
    padding: 0 10px; font: var(--ew-fw-regular) 14px var(--ew-font); background: #fff; color: var(--ew-dark);
  }
  .pl-input.search { width: 250px; max-width: 100%; }
  .pl-select.active { background: var(--ew-green-50); border-color: var(--ew-green); font-weight: 600; }
  .pl-link { all: unset; cursor: pointer; color: var(--ew-green-700); font-size: 13px; font-weight: 500; }
  .pl-link:hover { text-decoration: underline; }
  .pl-spacer { flex: 1; }

  /* Table */
  .pl-table { width: 100%; border-collapse: collapse; font-size: 14px; font-variant-numeric: tabular-nums; }
  .pl-table th {
    text-align: left; padding: 9px 10px; background: var(--ew-n-50);
    font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: 0.08em; text-transform: uppercase; color: var(--ew-n-500);
  }
  .pl-table td { padding: 10px; border-top: 1px solid var(--ew-n-100); vertical-align: middle; }
  .pl-table tbody tr { cursor: pointer; }
  .pl-table tbody tr:hover td { background: var(--ew-n-50); }
  .pl-desc { font-weight: 500; color: var(--ew-dark); }
  .pl-thumb {
    width: 40px; height: 32px; border-radius: var(--ew-r-sm); background: var(--ew-n-200); object-fit: cover; display: block;
  }

  /* Status badge (pill) and ball-in-court chip (squared) */
  .pl-badge {
    display: inline-flex; align-items: center; gap: 6px; padding: 3px 10px; border-radius: var(--ew-r-pill);
    font: var(--ew-fw-semibold) 12px/1.3 var(--ew-font); white-space: nowrap;
  }
  .pl-badge::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
  .pl-badge.new { background: var(--ew-n-100); color: var(--ew-n-700); }
  .pl-badge.open { background: var(--ew-performance-50); color: var(--ew-performance-700); }
  .pl-badge.rejected { background: var(--ew-danger-50); color: var(--ew-danger-700); }
  .pl-badge.ready_for_review { background: var(--ew-quality-50); color: var(--ew-quality-700); }
  .pl-badge.verified { background: var(--ew-green-50); color: var(--ew-green-700); }
  .pl-badge.closed { background: var(--ew-green); color: #fff; }
  .pl-bic {
    display: inline-block; padding: 3px 8px; border-radius: var(--ew-r-sm); border: 1px solid transparent;
    font: var(--ew-fw-medium) 12px/1.3 var(--ew-font); white-space: nowrap; max-width: 100%;
    overflow: hidden; text-overflow: ellipsis; vertical-align: middle;
  }
  .pl-bic.ew { background: var(--ew-dark); color: #fff; }
  .pl-bic.assignee { background: #fff; border-color: var(--ew-n-300); color: var(--ew-dark); }
  .pl-bic.ao { background: var(--ew-n-100); color: var(--ew-n-700); }
  .pl-bic.none { background: var(--ew-n-50); color: var(--ew-n-500); }
  .pl-bic.lg { font-size: 16px; font-weight: 600; padding: 6px 10px; }

  /* Drawer */
  .pl-scrim { position: fixed; inset: 0; background: rgba(0,0,0,0.35); z-index: 200; }
  .pl-drawer {
    position: fixed; top: 0; right: 0; bottom: 0; width: 600px; max-width: 100vw; background: #fff; z-index: 201;
    display: flex; flex-direction: column; box-shadow: var(--ew-shadow-3);
  }
  .pl-drawer-hd {
    padding: 14px 20px; border-bottom: 1px solid var(--ew-n-100); display: flex; align-items: center; gap: 10px;
  }
  .pl-drawer-no { font: var(--ew-fw-semibold) 17px/1 var(--ew-font); font-variant-numeric: tabular-nums; }
  .pl-drawer-bd { padding: 18px 20px 28px; overflow-y: auto; display: grid; gap: 16px; align-content: start; }
  .pl-drawer-ft {
    padding: 12px 20px; border-top: 1px solid var(--ew-n-100); display: flex; gap: 8px; justify-content: flex-end; flex-wrap: wrap;
  }
  .pl-item-title { margin: 0; font: var(--ew-fw-semibold) 22px/1.25 var(--ew-font); color: var(--ew-dark); overflow-wrap: anywhere; }
  .pl-item-where { font: var(--ew-fw-medium) 15px/1.3 var(--ew-font); color: var(--ew-n-500); margin-top: 4px; }
  .pl-panel { background: var(--ew-n-50); border-radius: var(--ew-r-md); padding: 12px 14px; }
  .pl-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  .pl-photos { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  .pl-photo {
    position: relative; aspect-ratio: 4 / 3; border-radius: var(--ew-r-md); overflow: hidden; background: var(--ew-n-200);
    display: flex; align-items: center; justify-content: center; color: var(--ew-n-500); font-size: 13px; text-align: center;
  }
  .pl-photo.after { background: var(--ew-green-100); }
  .pl-photo.empty { background: #fff; border: 1px dashed var(--ew-n-300); padding: 12px; }
  .pl-photo img { width: 100%; height: 100%; object-fit: cover; }
  .pl-photo-link {
    display: flex; width: 100%; height: 100%; align-items: center; justify-content: center;
    color: inherit; text-decoration: none; overflow-wrap: anywhere; padding: 0 8px; box-sizing: border-box;
  }
  .pl-photo-link:has(img) { padding: 0; }
  .pl-photo-tag {
    position: absolute; left: 8px; top: 8px; background: #fff; border-radius: var(--ew-r-sm); padding: 2px 6px;
    font: var(--ew-fw-semibold) 11px/1.3 var(--ew-font); color: var(--ew-dark);
  }
  .pl-photo-x {
    position: absolute; right: 6px; top: 6px; border: 0; border-radius: var(--ew-r-sm); background: rgba(255,255,255,0.9);
    cursor: pointer; font-size: 12px; padding: 2px 6px; color: var(--ew-danger-700);
  }
  /* Video clips (#518 phase 2): a poster that plays in place when tapped. */
  .pl-videos { display: grid; gap: 10px; }
  .pl-video { display: grid; gap: 6px; }
  .pl-video-poster {
    all: unset; box-sizing: border-box; cursor: pointer; position: relative; display: flex; align-items: center; justify-content: center;
    width: 100%; aspect-ratio: 16 / 9; border-radius: var(--ew-r-md); overflow: hidden; background: var(--ew-dark);
  }
  .pl-video-poster:focus-visible { outline: 2px solid var(--ew-green); outline-offset: 2px; }
  .pl-video-poster:disabled { cursor: progress; }
  .pl-video-poster img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; opacity: 0.85; }
  .pl-video-play {
    position: relative; width: 52px; height: 52px; border-radius: 50%; background: rgba(255,255,255,0.92);
    display: flex; align-items: center; justify-content: center; font-size: 20px; color: var(--ew-dark);
  }
  .pl-video-player { width: 100%; max-height: 60vh; border-radius: var(--ew-r-md); background: #000; }
  .pl-video-meta { font-size: 12px; color: var(--ew-n-500); gap: 8px; }
  .pl-video-meta a { color: var(--ew-green-700); }
  .pl-outdoor .pl-video-meta { color: var(--ew-dark); }
  .pl-strip { display: flex; gap: 6px; flex-wrap: wrap; }
  .pl-strip a { font-size: 12px; color: var(--ew-green-700); }
  .pl-facts { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 16px; }
  .pl-fact-v { font-size: 14px; color: var(--ew-dark); margin-top: 4px; overflow-wrap: anywhere; }
  .pl-notes { font-size: 14px; color: var(--ew-dark); white-space: pre-wrap; overflow-wrap: anywhere; }
  .pl-when { white-space: nowrap; font-variant-numeric: tabular-nums; }
  .pl-history { display: grid; gap: 0; }
  .pl-ev { display: grid; grid-template-columns: 10px 1fr; gap: 10px; padding-bottom: 12px; position: relative; }
  .pl-ev::before { content: ''; position: absolute; left: 4px; top: 12px; bottom: 0; width: 1px; background: var(--ew-n-200); }
  .pl-ev:last-child::before { display: none; }
  .pl-ev-dot { width: 10px; height: 10px; border-radius: 2px; background: var(--ew-n-300); margin-top: 4px; }
  .pl-ev:first-child .pl-ev-dot { background: var(--ew-dark); }
  .pl-ev-what { font: var(--ew-fw-medium) 15px/1.3 var(--ew-font); }
  .pl-ev-meta { font-size: 13px; color: var(--ew-n-500); }
  .pl-ev-note { font-size: 13px; color: var(--ew-dark); margin-top: 2px; white-space: pre-wrap; }

  /* Status stepper */
  .pl-stepper { display: flex; align-items: center; flex-wrap: wrap; row-gap: 6px; }
  .pl-stepper .pl-step-box { width: 28px; height: 28px; }

  /* Forms */
  .pl-form { display: grid; gap: 14px; }
  .pl-field { display: grid; gap: 5px; }
  .pl-field > span { font: var(--ew-fw-medium) 11px/1 var(--ew-font); letter-spacing: 0.08em; text-transform: uppercase; color: var(--ew-n-500); }
  .pl-field textarea, .pl-field input, .pl-field select {
    box-sizing: border-box; width: 100%; border: 1px solid var(--ew-n-300); border-radius: var(--ew-r-md);
    padding: 8px 10px; font: var(--ew-fw-regular) 14px var(--ew-font); background: #fff;
  }
  .pl-field textarea { min-height: 72px; resize: vertical; }
  .pl-grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  .pl-chips { display: flex; gap: 6px; flex-wrap: wrap; }
  .pl-chip {
    all: unset; cursor: pointer; padding: 6px 10px; border: 1px solid var(--ew-n-300); border-radius: var(--ew-r-md);
    font-size: 13px; background: #fff;
  }
  .pl-chip[aria-pressed='true'] { background: var(--ew-dark); border-color: var(--ew-dark); color: #fff; }
  .pl-tag {
    margin-left: 6px; padding: 2px 6px; border-radius: var(--ew-r-sm); background: var(--ew-n-100);
    color: var(--ew-n-500); font-size: 10px; letter-spacing: 0.06em;
  }
  .pl-trade.hidden > span, .pl-trade.hidden select { opacity: 0.55; }
  .pl-name { cursor: default; display: inline-flex; align-items: center; gap: 6px; }
  .pl-x {
    all: unset; cursor: pointer; padding: 0 4px; border-radius: var(--ew-r-sm); color: var(--ew-n-500);
    font-size: 16px; line-height: 1;
  }
  .pl-x:hover, .pl-x:focus-visible { background: var(--ew-n-100); color: var(--ew-dark); }
  .pl-x:disabled { cursor: default; opacity: 0.5; }
  .pl-switch { display: inline-flex; align-items: center; gap: 10px; cursor: pointer; font-size: 14px; }
  .pl-pick { display: grid; gap: 6px; }
  .pl-pick-row {
    all: unset; box-sizing: border-box; cursor: pointer; padding: 10px 12px; border: 1px solid var(--ew-n-200);
    border-radius: var(--ew-r-md); font-size: 14px; display: flex; justify-content: space-between; gap: 8px;
  }
  .pl-pick-row:hover { background: var(--ew-n-50); }
  .pl-pick-row[disabled] { opacity: 0.45; cursor: default; }
  .pl-preview { width: 100%; max-height: 220px; object-fit: contain; border-radius: var(--ew-r-md); background: var(--ew-n-100); }

  /* Publish steps */
  .pl-wizard-steps { display: flex; gap: 8px; margin-bottom: 12px; font-size: 13px; color: var(--ew-n-500); flex-wrap: wrap; }
  .pl-wizard-steps b { color: var(--ew-dark); }
  .pl-carry {
    display: grid; grid-template-columns: 24px 1fr auto auto; gap: 10px; align-items: center;
    padding: 10px 4px; border-bottom: 1px solid var(--ew-n-100); font-size: 14px;
  }
  .pl-changes { display: grid; grid-template-columns: 180px 1fr; gap: 8px 16px; font-size: 14px; }

  /* Empty and all-closed */
  .pl-empty { padding: 40px 24px; display: grid; justify-items: center; gap: 12px; text-align: center; }
  .pl-empty-tile {
    width: 56px; height: 56px; border-radius: var(--ew-r-md); background: var(--ew-green-50); color: var(--ew-green-700);
    display: flex; align-items: center; justify-content: center; font-size: 26px;
  }
  .pl-empty-tile.done { background: var(--ew-green); color: #fff; }
  .pl-empty h3 { margin: 0; font: var(--ew-fw-semibold) 20px/1.2 var(--ew-font); }
  .pl-empty p { margin: 0; max-width: 520px; color: var(--ew-n-500); font-size: 14px; }

  /* Bulk selection (design "Bulk bar"). */
  .pl-pick-col { width: 36px; text-align: center; }
  .pl-pick-col input { width: 16px; height: 16px; cursor: pointer; }
  .pl-table tr.picked td { background: var(--ew-green-50); }
  .pl-bulk { display: grid; gap: 8px; }
  .pl-bulk-bar {
    display: flex; align-items: center; gap: 8px; flex-wrap: wrap; padding: 8px 12px;
    background: var(--ew-dark); color: #fff; border-radius: var(--ew-r-md); font-size: 14px;
  }
  .pl-bulk-btn {
    all: unset; box-sizing: border-box; cursor: pointer; padding: 6px 10px; border-radius: var(--ew-r-md);
    border: 1px solid rgba(255,255,255,0.35); color: #fff; font: var(--ew-fw-medium) 13px/1 var(--ew-font);
  }
  .pl-bulk-btn:hover { background: rgba(255,255,255,0.1); }
  .pl-bulk-btn[aria-pressed='true'] { background: #fff; color: var(--ew-dark); }
  .pl-bulk-btn:disabled { opacity: 0.5; cursor: default; }
  .pl-bulk-btn:focus-visible { outline: 2px solid var(--ew-green-200); outline-offset: 2px; }
  .pl-bulk-panel { padding: 10px 12px; background: #fff; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); align-items: flex-end; }
  .pl-bulk-skipped { margin: 4px 0; padding-left: 18px; }

  /* Phone (design "Punch List: mobile", "Add item: mobile"). 44px+ targets, 16px+ text. */
  .pl-phone { display: grid; grid-template-columns: minmax(0, 1fr); gap: 12px; padding-bottom: 96px; font-size: 16px; }
  .pl-phone-stage { display: grid; grid-template-columns: auto 1fr; gap: 6px 10px; align-items: center; }
  .pl-phone-stage .pl-bar { grid-column: 1 / -1; }
  .pl-phone-stage-chip {
    padding: 4px 8px; border-radius: var(--ew-r-sm); background: var(--ew-green-50); color: var(--ew-green-900);
    font: var(--ew-fw-semibold) 12px/1 var(--ew-font); letter-spacing: 0.06em; text-transform: uppercase;
  }
  .pl-phone-tiles { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
  .pl-phone-tile {
    all: unset; box-sizing: border-box; cursor: pointer; display: grid; gap: 4px; padding: 10px 12px; min-height: 72px;
    background: #fff; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md);
  }
  .pl-phone-tile.active { border: 2px solid var(--ew-dark); padding: 9px 11px; }
  .pl-phone-tile:focus-visible, .pl-phone-chip:focus-visible, .pl-room-row:focus-visible, .pl-phone-btn:focus-visible,
  .pl-trade-tile:focus-visible, .pl-room-cell:focus-visible, .pl-sheet-pick:focus-visible {
    outline: 2px solid var(--ew-green); outline-offset: 2px;
  }
  .pl-phone-tile-v { font: var(--ew-fw-semibold) 26px/1 var(--ew-font); font-variant-numeric: tabular-nums; }
  .pl-phone-tile-l { font-size: 13px; color: var(--ew-n-500); }
  .pl-phone-chips { display: flex; gap: 8px; overflow-x: auto; padding-bottom: 2px; scrollbar-width: none; }
  .pl-phone-chips::-webkit-scrollbar { display: none; }
  .pl-phone-chips.wrap { flex-wrap: wrap; overflow: visible; }
  .pl-phone-chip {
    all: unset; box-sizing: border-box; cursor: pointer; flex: none; min-height: 44px; padding: 0 14px;
    display: inline-flex; align-items: center; border: 1px solid var(--ew-n-300); border-radius: var(--ew-r-md);
    background: #fff; font: var(--ew-fw-semibold) 15px/1 var(--ew-font); white-space: nowrap;
  }
  .pl-phone-chip.active { background: var(--ew-dark); border-color: var(--ew-dark); color: #fff; }
  .pl-phone-count { font: var(--ew-fw-semibold) 15px/1 var(--ew-font); }
  .pl-room { overflow: hidden; }
  .pl-room-hd { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 12px 14px; background: var(--ew-n-50); }
  .pl-room-title { font: var(--ew-fw-semibold) 18px/1.2 var(--ew-font); }
  .pl-room-row {
    all: unset; box-sizing: border-box; cursor: pointer; width: 100%; display: grid; grid-template-columns: 56px 1fr 16px;
    gap: 12px; align-items: center; padding: 10px 14px; border-top: 1px solid var(--ew-n-100);
  }
  .pl-room-thumb { display: block; width: 56px; height: 56px; object-fit: cover; border-radius: var(--ew-r-sm); background: var(--ew-n-200); }
  .pl-room-main { display: grid; gap: 6px; min-width: 0; }
  .pl-room-desc { font: var(--ew-fw-medium) 16px/1.25 var(--ew-font); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .pl-room-meta { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; font-size: 13px; }
  .pl-room-chev { font-size: 22px; color: var(--ew-n-400); }
  .pl-phone-btn {
    all: unset; box-sizing: border-box; cursor: pointer; min-height: 44px; padding: 0 16px; display: inline-flex;
    align-items: center; justify-content: center; gap: 6px; border-radius: var(--ew-r-md); text-align: center;
    font: var(--ew-fw-semibold) 16px/1.1 var(--ew-font);
  }
  .pl-phone-btn.primary { background: var(--ew-green); color: #fff; }
  .pl-phone-btn.secondary { background: #fff; color: var(--ew-dark); border: 1px solid var(--ew-n-300); }
  .pl-phone-btn.wide { width: 100%; }
  .pl-phone-btn.tall { min-height: 56px; font-size: 18px; }
  .pl-phone-btn:disabled { opacity: 0.45; cursor: default; }
  .pl-phone-more {
    all: unset; box-sizing: border-box; cursor: pointer; min-height: 48px; text-align: center; border: 1px dashed var(--ew-n-300);
    border-radius: var(--ew-r-md); color: var(--ew-n-500); font-size: 15px;
  }
  .pl-phone-bottom {
    position: fixed; left: 0; right: 0; bottom: 0; z-index: 90; padding: 12px 16px 28px; /* under the app's mobile menu (99) */
    background: #fff; border-top: 1px solid var(--ew-n-200);
  }
  .pl-phone-bottom .pl-phone-btn { min-height: 56px; font-size: 18px; }
  .pl-bsheet-scrim { position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 220; }
  .pl-bsheet {
    position: fixed; left: 0; right: 0; bottom: 0; z-index: 221; max-height: 75vh; display: grid; grid-template-rows: auto auto 1fr;
    gap: 10px; padding: 8px 16px 24px; background: #fff; border-radius: 8px 8px 0 0; font-size: 16px;
  }
  .pl-bsheet-grab { justify-self: center; width: 40px; height: 4px; border-radius: 2px; background: var(--ew-n-300); }
  .pl-bsheet-body { overflow-y: auto; display: grid; gap: 6px; align-content: start; }
  .pl-sheet-pick {
    all: unset; box-sizing: border-box; cursor: pointer; min-height: 48px; display: flex; align-items: center;
    justify-content: space-between; gap: 8px; padding: 0 12px; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md);
  }
  .pl-room-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
  .pl-room-cell {
    all: unset; box-sizing: border-box; cursor: pointer; min-height: 56px; display: grid; place-content: center; gap: 2px;
    text-align: center; padding: 6px; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); font-size: 12px;
  }
  .pl-room-cell[aria-pressed='true'] { background: var(--ew-green-50); border: 2px solid var(--ew-green); }
  .pl-room-cell-name { font: var(--ew-fw-semibold) 16px/1.1 var(--ew-font); overflow: hidden; text-overflow: ellipsis; }

  .pl-padd {
    position: fixed; inset: 0; z-index: 205; display: grid; grid-template-rows: auto auto minmax(0, 1fr) auto;
    grid-template-columns: minmax(0, 1fr);
    background: #fff; font-family: var(--ew-font); color: var(--ew-dark); font-size: 16px;
  }
  .pl-padd-hd { display: flex; align-items: center; gap: 10px; padding: 8px 12px; border-bottom: 1px solid var(--ew-n-200); }
  .pl-padd-title { flex: 1; display: grid; gap: 2px; }
  .pl-phone-icon {
    all: unset; box-sizing: border-box; cursor: pointer; width: 48px; height: 48px; display: grid; place-items: center;
    border-radius: var(--ew-r-md); font-size: 28px; line-height: 1;
  }
  .pl-phone-icon.big { width: 56px; height: 56px; border: 1px solid var(--ew-n-300); font-size: 26px; }
  .pl-phone-icon:disabled { opacity: 0.45; }
  .pl-padd-progress { display: grid; grid-template-columns: repeat(4, 1fr); gap: 4px; padding: 0 12px; }
  .pl-padd-progress > span { height: 4px; border-radius: 2px; background: var(--ew-n-200); }
  .pl-padd-progress > span.done { background: var(--ew-green); }
  .pl-padd-bd { overflow-y: auto; min-height: 0; padding: 16px; display: grid; grid-template-columns: minmax(0, 1fr); gap: 14px; align-content: start; }
  .pl-padd-added {
    justify-self: start; padding: 6px 10px; border-radius: var(--ew-r-sm); background: var(--ew-green-50); color: var(--ew-green-900);
    font: var(--ew-fw-semibold) 13px/1 var(--ew-font);
  }
  .pl-padd-cam { display: grid; gap: 14px; padding-top: 8px; }
  .pl-padd-cam-title { font: var(--ew-fw-semibold) 20px/1.2 var(--ew-font); }
  .pl-padd-req {
    margin-left: 6px; padding: 2px 6px; border-radius: var(--ew-r-sm); background: var(--ew-safety-50); color: var(--ew-safety-700);
    font-size: 12px; vertical-align: middle;
  }
  .pl-padd-roomchip { justify-self: start; padding: 6px 10px; border-radius: var(--ew-r-sm); background: var(--ew-n-100); font-size: 14px; }
  .pl-padd-thumb { width: 72px; height: 72px; object-fit: cover; border-radius: var(--ew-r-sm); }
  .pl-field textarea.pl-padd-text { font-size: 18px; min-height: 96px; }
  .pl-field textarea.pl-padd-text.small { font-size: 16px; min-height: 72px; }
  .pl-trade-tiles { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
  .pl-trade-tile {
    all: unset; box-sizing: border-box; cursor: pointer; min-height: 64px; display: grid; place-items: center; padding: 6px;
    text-align: center; border: 1px solid var(--ew-n-300); border-radius: var(--ew-r-md); font: var(--ew-fw-medium) 15px/1.2 var(--ew-font);
    overflow-wrap: anywhere;
  }
  .pl-trade-tile[aria-pressed='true'] { background: var(--ew-green-50); border: 2px solid var(--ew-green); }
  .pl-padd-assigned { padding: 12px 14px; display: grid; gap: 10px; }
  .pl-field select.pl-phone-select, .pl-field input.pl-phone-select, select.pl-phone-select { min-height: 48px; font-size: 16px; }
  .pl-room-stepper { display: grid; grid-template-columns: 56px minmax(0, 1fr) 56px; gap: 8px; align-items: center; }
  .pl-room-value {
    box-sizing: border-box; width: 100%; height: 56px; text-align: center; border: 2px solid var(--ew-dark);
    border-radius: var(--ew-r-md); font: var(--ew-fw-semibold) 24px/1 var(--ew-font);
  }
  .pl-padd-defaults {
    display: grid; grid-template-columns: 1fr auto; gap: 2px 10px; padding: 10px 12px; background: var(--ew-n-50);
    border-radius: var(--ew-r-md); font-size: 14px;
  }
  .pl-padd-defaults > .pl-link { grid-row: 1 / span 2; grid-column: 2; align-self: center; }
  .pl-padd-ft {
    display: grid; grid-template-columns: 1fr 1.4fr; gap: 10px; padding: 12px 16px 28px; border-top: 1px solid var(--ew-n-200);
  }
  .pl-padd-ft .pl-phone-btn { min-height: 56px; }
  .pl-toast {
    position: fixed; left: 16px; right: 16px; bottom: 104px; z-index: 230; padding: 12px 14px; border-radius: var(--ew-r-md);
    background: var(--ew-dark); color: #fff; box-shadow: var(--ew-shadow-3); font-size: 15px;
  }

  .pl-phone-stage { grid-template-columns: auto 1fr auto; }
  .pl-outdoor-btn {
    all: unset; box-sizing: border-box; cursor: pointer; width: 48px; height: 48px; display: grid; place-items: center;
    border: 1px solid var(--ew-n-300); border-radius: var(--ew-r-md); background: #fff; font-size: 22px; line-height: 1;
  }
  .pl-outdoor-btn.on { background: var(--ew-safety); border-color: #000; color: #000; }
  .pl-outdoor-btn:focus-visible { outline: 2px solid var(--ew-green); outline-offset: 2px; }

  /* Outdoor high contrast (design "Outdoor high contrast (mobile)"): white,
     black text, dark secondary text, 2px dark borders, solid status fills. */
  .pl-outdoor, .pl-outdoor .pl-card, .pl-outdoor .pl-drawer, .pl-outdoor .pl-padd, .pl-outdoor .pl-bsheet,
  .pl-outdoor .pl-room-hd, .pl-outdoor .pl-panel, .pl-outdoor .pl-padd-defaults, .pl-outdoor .pl-phone-tile,
  .pl-outdoor .pl-phone-bottom, .pl-outdoor .pl-drawer-hd, .pl-outdoor .pl-drawer-ft { background: #fff; color: #000; }
  .pl-outdoor .pl-muted, .pl-outdoor .pl-caption, .pl-outdoor .pl-phone-tile-l, .pl-outdoor .pl-room-chev,
  .pl-outdoor .pl-field > span, .pl-outdoor .pl-item-where { color: var(--ew-dark); }
  .pl-outdoor .pl-card, .pl-outdoor .pl-phone-tile, .pl-outdoor .pl-phone-chip, .pl-outdoor .pl-trade-tile,
  .pl-outdoor .pl-phone-btn.secondary, .pl-outdoor .pl-room-cell, .pl-outdoor .pl-sheet-pick, .pl-outdoor .pl-phone-icon.big,
  .pl-outdoor .pl-room-value, .pl-outdoor .pl-outdoor-btn, .pl-outdoor .pl-bic,
  .pl-outdoor .pl-field input, .pl-outdoor .pl-field select, .pl-outdoor .pl-field textarea { border: 2px solid var(--ew-n-700); }
  .pl-outdoor .pl-outdoor-btn.on { border-color: #000; }
  .pl-outdoor .pl-room-row { border-top: 2px solid var(--ew-n-700); }
  .pl-outdoor .pl-phone-tile.active, .pl-outdoor .pl-trade-tile[aria-pressed='true'],
  .pl-outdoor .pl-room-cell[aria-pressed='true'] { border: 3px solid #000; }
  .pl-outdoor .pl-phone-chip.active { background: #000; border-color: #000; color: #fff; }
  .pl-outdoor .pl-badge.new { background: var(--ew-n-700); color: #fff; }
  .pl-outdoor .pl-badge.open { background: var(--ew-performance); color: #000; }
  .pl-outdoor .pl-badge.rejected { background: var(--ew-danger); color: #fff; }
  .pl-outdoor .pl-badge.ready_for_review { background: var(--ew-quality); color: #fff; }
  .pl-outdoor .pl-badge.verified { background: var(--ew-green); color: #fff; }
  .pl-outdoor .pl-badge.closed { background: #000; color: #fff; }

  /* The item drawer on a phone: full width, bigger targets, the actions fill the footer. */
  @media (max-width: 768px) {
    .pl-drawer { width: 100vw; }
    .pl-drawer-bd { padding: 14px 16px 24px; font-size: 16px; }
    .pl-item-title { font-size: 22px; }
    .pl-drawer-ft { padding: 12px 16px 24px; }
    .pl-drawer-ft > button, .pl-panel-actions > button { flex: 1 1 auto; min-height: 52px; font-size: 16px; }
    .pl-drawer .pl-chip, .pl-drawer .pl-pick-row { min-height: 44px; }
    .pl-drawer .pl-link { min-height: 44px; display: inline-flex; align-items: center; }
  }

  /* Drawing pins (#518 phase 3, design "Drawing pins"): a portal on <body>, desktop only. */
  /* A column, not a grid with a fixed row count: the toolbar and the optional
     banners (placing, removed set, newer revision) stack above, and the body
     takes whatever height is left however many banners there are. A grid of
     "auto auto auto 1fr" put the body in the second, auto-sized row when no
     banner showed, so a tall sheet ran past the screen instead of scrolling (review). */
  .pl-dw {
    position: fixed; inset: 0; z-index: 210; display: flex; flex-direction: column;
    background: var(--ew-n-200); font-family: var(--ew-font); color: var(--ew-dark);
  }
  .pl-dw-bar {
    display: flex; align-items: center; gap: 12px; flex-wrap: wrap; padding: 12px 20px;
    background: #fff; border-bottom: 1px solid var(--ew-n-200);
  }
  .pl-dw-bar .pl-select { max-width: 420px; }
  .pl-dw-bar, .pl-dw-banner { flex: none; }
  .pl-dw-banner {
    display: flex; align-items: center; gap: 12px; padding: 8px 20px; font-size: 14px;
    background: var(--ew-quality-50); color: var(--ew-quality-700); border-bottom: 1px solid var(--ew-n-200);
  }
  .pl-dw-banner.muted { background: var(--ew-n-50); color: var(--ew-dark); }
  .pl-dw-body { flex: 1; display: grid; grid-template-columns: 240px minmax(0, 1fr) 280px; grid-template-rows: minmax(0, 1fr); min-height: 0; }
  .pl-dw-sheets {
    background: #fff; border-right: 1px solid var(--ew-n-200); overflow-y: auto; display: grid; align-content: start;
  }
  .pl-dw-sheet {
    all: unset; box-sizing: border-box; cursor: pointer; display: flex; justify-content: space-between; gap: 8px;
    padding: 8px 12px; border-left: 3px solid transparent; font-size: 13px; border-bottom: 1px solid var(--ew-n-100);
  }
  .pl-dw-sheet:hover { background: var(--ew-n-50); }
  .pl-dw-sheet:focus-visible { outline: 2px solid var(--ew-green); outline-offset: -2px; }
  .pl-dw-sheet.active { background: var(--ew-green-50); border-left-color: var(--ew-green); font-weight: 600; }
  /* The vertical scrollbar is always there: the sheet is drawn to this box's width,
     so a scrollbar that came and went with the sheet's height would change the width
     and redraw the sheet, and a height within a scrollbar's width of the screen's
     would flip it back and forth (review). */
  .pl-dw-scroll { overflow-x: auto; overflow-y: scroll; padding: 24px; min-width: 0; }
  .pl-dw-page { position: relative; margin: 0 auto; background: #fff; box-shadow: var(--ew-shadow-2); }
  .pl-dw-page canvas { display: block; }
  .pl-dw-page.dropping, .pl-dw-page.dropping .pl-pin { cursor: crosshair; }
  /* Until the sheet in view has finished drawing: not clickable, and a little faded to show it. */
  .pl-dw-page.loading { pointer-events: none; opacity: 0.6; }
  .pl-dw-side {
    background: #fff; border-left: 1px solid var(--ew-n-200); overflow-y: auto; padding: 16px;
    display: grid; gap: 12px; align-content: start; font-size: 14px;
  }
  .pl-dw-legend { font-size: 12px; }
  /* A pin: a square in its item's solid status color, its number on it. */
  .pl-pin {
    all: unset; box-sizing: border-box; position: absolute; transform: translate(-50%, -50%); cursor: pointer;
    min-width: 24px; height: 24px; padding: 0 4px; border-radius: 2px; display: flex; align-items: center; justify-content: center;
    font: var(--ew-fw-semibold) 11px/1 var(--ew-font); font-variant-numeric: tabular-nums;
    color: #fff; background: var(--ew-n-500); box-shadow: 0 1px 3px rgba(0,0,0,0.35);
  }
  .pl-pin.open { background: var(--ew-performance); color: #000; }
  .pl-pin.rejected { background: var(--ew-danger); }
  .pl-pin.ready_for_review { background: var(--ew-quality); }
  .pl-pin.verified { background: var(--ew-green-700); }
  .pl-pin.closed { background: var(--ew-green); }
  .pl-pin:focus-visible { outline: 2px solid var(--ew-dark); outline-offset: 2px; }
  .pl-pin.selected { box-shadow: 0 0 0 3px #fff, 0 0 0 5px var(--ew-dark); z-index: 1; }
  .pl-pin.dropped { background: #fff; color: var(--ew-dark); border: 2px dashed var(--ew-dark); cursor: default; }

  /* Drawing pins on a phone (#518 phase 3, decided 2026-10-09): a portal on <body>.
     Above Add Item (205), so Pin on Drawing opens over the form; under the bottom
     sheets (220), so the sheet list and a pin's item open over it. */
  .pl-pd {
    position: fixed; inset: 0; z-index: 212; display: flex; flex-direction: column;
    /* A quick double tap on a button mustn't zoom the page, nor a drag here scroll it. */
    touch-action: manipulation; overscroll-behavior: contain;
    background: var(--ew-n-200); font-family: var(--ew-font); color: var(--ew-dark); font-size: 16px;
  }
  .pl-pd > * { flex: none; }
  .pl-pd-hd { display: flex; align-items: center; gap: 8px; padding: 8px 12px; background: #fff; border-bottom: 1px solid var(--ew-n-200); }
  .pl-pd-title { display: grid; gap: 2px; min-width: 0; }
  .pl-pd-sheet { text-align: left; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; }
  /* The sheet area: the fingers move the sheet, so the browser mustn't scroll or zoom the page. */
  .pl-pd-area { flex: 1; position: relative; overflow: hidden; touch-action: none; user-select: none; -webkit-user-select: none; }
  .pl-pd-msg { margin: 16px; }
  .pl-pd-page { position: absolute; left: 0; top: 0; transform-origin: 0 0; background: #fff; box-shadow: var(--ew-shadow-2); }
  /* While fingers move, the sheet drawn at the last zoom is stretched to fit; it is
     drawn again sharp when they lift. */
  .pl-pd-page canvas { display: block; width: 100% !important; height: 100% !important; }
  .pl-pd-page .pl-pin { min-width: 28px; height: 28px; font-size: 12px; }
  /* Tapped with a finger, maybe gloved: the pin answers 8 px around it too, a 44 px target
     like the rest of the phone screens, without covering more of the drawing (review). */
  .pl-pd-page .pl-pin::after { content: ''; position: absolute; inset: -8px; }
  .pl-pin.moving { opacity: 0.35; }
  /* The crosshair: fixed at the middle, over the sheet, never in the way of a finger. */
  .pl-pd-cross { position: absolute; left: 50%; top: 50%; width: 44px; height: 44px; margin: -22px 0 0 -22px; pointer-events: none; }
  .pl-pd-cross::before, .pl-pd-cross::after { content: ''; position: absolute; background: var(--ew-danger); box-shadow: 0 0 0 1px #fff; }
  .pl-pd-cross::before { left: 21px; top: 0; width: 2px; height: 44px; }
  .pl-pd-cross::after { top: 21px; left: 0; height: 2px; width: 44px; }
  .pl-pd-cross.off::before, .pl-pd-cross.off::after { background: var(--ew-n-500); }
  .pl-pd-zoom { position: absolute; right: 12px; bottom: 12px; display: grid; gap: 8px; }
  .pl-pd-zoom .pl-phone-icon { background: #fff; border: 1px solid var(--ew-n-300); box-shadow: var(--ew-shadow-2); }
  .pl-pd-ft {
    display: flex; align-items: center; justify-content: space-between; gap: 8px;
    padding: 10px 12px calc(10px + env(safe-area-inset-bottom)); background: #fff; border-top: 1px solid var(--ew-n-200);
  }
  .pl-pd-ft .pl-phone-btn { min-height: 52px; }
  .pl-pd-ft .pl-phone-btn.primary { flex: 1; }
  /* Add Item opened from the phone's drawing (Add Item Here) goes over it, and the
     drawing opened from Add Item (Pin on Drawing, Move) over that: drawing 212, Add
     Item 205 or 215, its own drawing 218, the bottom sheets 220 (review). */
  .pl-padd.on-top { z-index: 215; }
  .pl-pd.over-add { z-index: 218; }
  /* In the sun: a darker surround, so the white sheet still stands out from it
     (.pl-outdoor alone would make the surround white too). */
  .pl-pd.pl-outdoor { background: var(--ew-n-300); }

  /* Sub sheets (design "Export per subcontractor"). A portal on <body>. */
  .pl-sheets {
    position: fixed; inset: 0; z-index: 210; display: grid; grid-template-rows: auto 1fr;
    background: var(--ew-n-200); font-family: var(--ew-font); color: var(--ew-dark);
  }
  .pl-sheets-bar {
    display: flex; align-items: center; gap: 12px; flex-wrap: wrap; padding: 12px 20px;
    background: #fff; border-bottom: 1px solid var(--ew-n-200);
  }
  .pl-sheets-body { display: grid; grid-template-columns: 260px 1fr; min-height: 0; }
  .pl-sheets-side {
    background: #fff; border-right: 1px solid var(--ew-n-200); overflow-y: auto; padding: 16px;
    display: grid; gap: 12px; align-content: start; font-size: 14px;
  }
  .pl-sheets-pick { display: grid; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); overflow: hidden; }
  .pl-sheets-sub {
    all: unset; box-sizing: border-box; cursor: pointer; display: flex; justify-content: space-between; gap: 8px;
    padding: 10px 12px; border-left: 3px solid transparent; font-size: 14px; font-variant-numeric: tabular-nums;
  }
  .pl-sheets-sub + .pl-sheets-sub { border-top: 1px solid var(--ew-n-100); }
  .pl-sheets-sub:hover { background: var(--ew-n-50); }
  .pl-sheets-sub:disabled { cursor: default; opacity: 0.6; }
  .pl-sheets-sub:focus-visible { outline: 2px solid var(--ew-green); outline-offset: -2px; }
  .pl-sheets-sub.active { background: var(--ew-green-50); border-left-color: var(--ew-green); font-weight: 600; }
  .pl-sheets-info { padding: 12px; }
  .pl-sheets-info p { margin: 6px 0 0; color: var(--ew-n-500); font-size: 13px; }
  .pl-sheets-preview { overflow-y: auto; padding: 24px; display: grid; gap: 24px; justify-content: center; align-content: start; }
  .pl-sheet {
    width: 680px; box-sizing: border-box; background: #fff; padding: 36px 40px; box-shadow: var(--ew-shadow-2);
    display: flex; flex-direction: column; gap: 16px;
  }
  .pl-sheet-hd { display: flex; justify-content: space-between; gap: 16px; padding-bottom: 14px; border-bottom: 2px solid var(--ew-dark); }
  .pl-sheet-stage {
    font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: 0.08em; text-transform: uppercase; color: var(--ew-green-700);
  }
  .pl-sheet-hd h2 { margin: 6px 0 4px; font: var(--ew-fw-semibold) 24px/1.2 var(--ew-font); }
  .pl-sheet-meta { font-size: 13px; color: var(--ew-n-500); line-height: 1.5; }
  .pl-sheet-logo { height: 30px; width: auto; }
  .pl-sheet-note {
    padding: 10px 12px; background: var(--ew-n-50); border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md);
    font-size: 13px; line-height: 1.5;
  }
  .pl-sheet-items { display: grid; }
  .pl-sheet-row {
    display: grid; grid-template-columns: 120px 1fr 88px; gap: 14px; padding: 12px 0;
    border-top: 1px solid var(--ew-n-100); break-inside: avoid;
  }
  .pl-sheet-photo { display: block; width: 120px; height: 90px; object-fit: cover; background: var(--ew-n-200); border-radius: var(--ew-r-sm); }
  .pl-sheet-where { font: var(--ew-fw-semibold) 13px/1.3 var(--ew-font); font-variant-numeric: tabular-nums; }
  .pl-sheet-desc { font-size: 15px; margin-top: 3px; overflow-wrap: anywhere; }
  .pl-sheet-notes { font-size: 13px; color: var(--ew-n-500); margin-top: 3px; white-space: pre-wrap; overflow-wrap: anywhere; }
  .pl-sheet-sub { font-size: 12px; color: var(--ew-n-500); margin-top: 4px; }
  .pl-sheet-due { font-size: 13px; font-variant-numeric: tabular-nums; display: grid; gap: 8px; align-content: start; }
  .pl-sheet-check { font-size: 14px; }
  .pl-sheet-ft {
    display: flex; justify-content: space-between; gap: 12px; padding-top: 10px; margin-top: auto;
    border-top: 1px solid var(--ew-n-200); font-size: 11px; color: var(--ew-n-500);
  }
  /* Close-out report: the sheets' page, with the record's facts, counts per
     assignee, and before and after photos on every item. */
  .pl-report { gap: 14px; }
  .pl-report-facts { display: grid; grid-template-columns: max-content 1fr; gap: 4px 16px; margin: 0; font-size: 13px; }
  .pl-report-facts dt { color: var(--ew-n-500); }
  .pl-report-facts dd { margin: 0; overflow-wrap: anywhere; }
  .pl-report-counts {
    display: flex; flex-wrap: wrap; gap: 12px 28px; padding: 12px 0;
    border-top: 1px solid var(--ew-n-200); border-bottom: 1px solid var(--ew-n-200);
  }
  .pl-report-count { font: var(--ew-fw-semibold) 22px/1.1 var(--ew-font); font-variant-numeric: tabular-nums; }
  .pl-report-subs { width: 100%; border-collapse: collapse; font-size: 13px; font-variant-numeric: tabular-nums; }
  .pl-report-subs th {
    text-align: left; font-weight: var(--ew-fw-semibold); color: var(--ew-n-500);
    padding: 4px 8px 4px 0; border-bottom: 1px solid var(--ew-n-200);
  }
  .pl-report-subs td { padding: 4px 8px 4px 0; border-bottom: 1px solid var(--ew-n-100); overflow-wrap: anywhere; }
  .pl-report-subs tr { break-inside: avoid; }
  .pl-report-subs th + th, .pl-report-subs td + td { text-align: right; width: 88px; }
  .pl-report-row { grid-template-columns: 120px 120px 1fr; }
  .pl-report-photo { margin: 0; display: grid; gap: 4px; align-content: start; }
  .pl-report-photo figcaption { font-size: 11px; color: var(--ew-n-500); }
  .pl-report-signoffs { list-style: none; margin: 6px 0 0; padding: 0; display: grid; gap: 2px; font-size: 12px; }
  @media (max-width: 900px) {
    .pl-sheets-body { grid-template-columns: 1fr; grid-template-rows: auto 1fr; }
    .pl-sheets-side { border-right: 0; border-bottom: 1px solid var(--ew-n-200); max-height: 40vh; }
    .pl-sheets-preview { padding: 12px; }
    .pl-sheet { width: 100%; padding: 20px; }
    .pl-sheet-row { grid-template-columns: 88px 1fr; }
    .pl-sheet-photo { width: 88px; height: 66px; }
    .pl-sheet-due { grid-column: 2; grid-template-columns: auto auto; justify-content: start; gap: 16px; }
    .pl-report-row { grid-template-columns: 88px 88px 1fr; }
  }
  /* Print only the sheets: everything else on <body> is hidden, and each
     sheet starts a page. Colors kept so the photo boxes and rules print. */
  @media print {
    @page { size: letter; margin: 0.5in; }
    body > *:not(.pl-sheets) { display: none !important; }
    body { background: #fff !important; }
    .pl-sheets { position: static; display: block; background: #fff; }
    .pl-sheets-bar, .pl-sheets-side { display: none !important; }
    .pl-sheets-body, .pl-sheets-preview { display: block; overflow: visible; padding: 0; }
    .pl-sheet { width: auto; box-shadow: none; padding: 0; break-after: page; }
    .pl-sheet:last-child { break-after: auto; }
    /* The report runs many pages: blocks, not a flex column, which some print
       engines don't break across pages cleanly (review). */
    .pl-report { display: block; }
    .pl-report > * + * { margin-top: 14px; }
    .pl-sheets * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
`;

export function PunchStyles() {
  return <style>{css}</style>;
}
